import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, DollarSign, CreditCard, Building2, ExternalLink, 
  CheckCircle, AlertTriangle, Info, Wallet, ArrowRight, Star, Copy,
  Plus, Trash2, FileText, Calendar, TrendingUp, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeeConversion = () => {
  const { token } = useAuth();
  const [selectedMethod, setSelectedMethod] = useState('coinbase');
  const [conversions, setConversions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newConversion, setNewConversion] = useState({
    conversion_type: 'eth_to_usd',
    from_amount: '',
    from_currency: 'ETH',
    to_amount: '',
    to_currency: 'USD',
    method: 'coinbase',
    tx_hash: '',
    notes: ''
  });

  useEffect(() => {
    fetchConversions();
  }, []);

  const fetchConversions = async () => {
    try {
      const [convRes, summaryRes] = await Promise.all([
        fetch(`${API}/conversions?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/conversions/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (convRes.ok) {
        const data = await convRes.json();
        setConversions(data.conversions || []);
      }
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching conversions:', error);
    }
  };

  const handleAddConversion = async () => {
    if (!newConversion.from_amount || !newConversion.to_amount) {
      toast.error('Please enter both amounts');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/conversions/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newConversion,
          from_amount: parseFloat(newConversion.from_amount),
          to_amount: parseFloat(newConversion.to_amount)
        })
      });

      if (response.ok) {
        toast.success('Conversion recorded!');
        setShowAddForm(false);
        setNewConversion({
          conversion_type: 'eth_to_usd',
          from_amount: '',
          from_currency: 'ETH',
          to_amount: '',
          to_currency: 'USD',
          method: 'coinbase',
          tx_hash: '',
          notes: ''
        });
        fetchConversions();
      } else {
        toast.error('Failed to record conversion');
      }
    } catch (error) {
      toast.error('Error recording conversion');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversion = async (id) => {
    if (!window.confirm('Delete this conversion record?')) return;
    
    try {
      const response = await fetch(`${API}/conversions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Conversion deleted');
        fetchConversions();
      }
    } catch (error) {
      toast.error('Error deleting conversion');
    }
  };

  const exportToCSV = () => {
    if (conversions.length === 0) {
      toast.error('No conversions to export');
      return;
    }

    const headers = ['ID', 'Date', 'From Amount', 'From Currency', 'To Amount', 'To Currency', 'Method', 'TX Hash', 'Notes', 'Recorded By'];
    const rows = conversions.map(c => [
      c.id,
      new Date(c.recorded_at).toLocaleDateString(),
      c.from_amount,
      c.from_currency,
      c.to_amount,
      c.to_currency,
      c.method,
      c.tx_hash || '',
      c.notes || '',
      c.recorded_by || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bethereum_conversions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported!');
  };

  const exportToPDF = () => {
    if (conversions.length === 0) {
      toast.error('No conversions to export');
      return;
    }

    // Create printable HTML content
    const totalUSD = conversions.reduce((sum, c) => c.to_currency === 'USD' ? sum + c.to_amount : sum, 0);
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bethereum Conversion Report</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #0a1628; border-bottom: 2px solid #b8860b; padding-bottom: 10px; }
          .summary { background: #f5f5f5; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
          .summary h3 { margin: 0 0 10px 0; color: #b8860b; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #0a1628; color: white; padding: 12px; text-align: left; }
          td { padding: 10px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .footer { margin-top: 40px; font-size: 12px; color: #666; text-align: center; }
          .amount { font-weight: bold; }
          .total { font-size: 24px; color: #10b981; }
        </style>
      </head>
      <body>
        <h1>Bethereum - Conversion Report</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        
        <div class="summary">
          <h3>Summary</h3>
          <p>Total Records: <strong>${conversions.length}</strong></p>
          <p>Total USD Converted: <span class="total">$${totalUSD.toLocaleString()}</span></p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>From</th>
              <th>To</th>
              <th>Method</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${conversions.map(c => `
              <tr>
                <td>${new Date(c.recorded_at).toLocaleDateString()}</td>
                <td class="amount">${c.from_amount} ${c.from_currency}</td>
                <td class="amount">${c.to_amount?.toLocaleString()} ${c.to_currency}</td>
                <td>${c.method}</td>
                <td>${c.notes || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Bethereum Private Wealth Management - For Accredited Investors Only</p>
          <p>This document is for record-keeping purposes only.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF export opened in new window');
  };

  const CONVERSION_METHODS = [
    {
      id: 'coinbase',
      name: 'Coinbase',
      description: 'Recommended - Convert ETH to USD via your existing account',
      fee: '0.5-1.5%',
      time: '1-3 business days',
      minAmount: '$1',
      features: ['Lowest fees', 'Bank transfer', 'Instant sell', 'You have an account'],
      recommended: true,
      color: 'blue'
    },
    {
      id: 'uniswap',
      name: 'Swap to USDC',
      description: 'Convert to stablecoins on Uniswap (stay on-chain)',
      fee: '0.3-1%',
      time: 'Instant',
      minAmount: 'Gas fees only',
      features: ['Instant conversion', 'Stay on-chain', 'No KYC'],
      color: 'pink'
    },
    {
      id: 'moonpay',
      name: 'MoonPay Off-Ramp',
      description: 'Direct crypto to bank (requires business setup)',
      fee: '1-4.5%',
      time: '1-3 business days',
      minAmount: '$20',
      features: ['No exchange needed', 'Direct to bank'],
      color: 'purple'
    }
  ];

  const TREASURY_WALLET = "0x2FB355B99b2060e42DB50D99393D00f47A41d322";
  const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const handleUniswapSwap = (from, to) => {
    let url;
    if (from === 'BETH') {
      url = `https://app.uniswap.org/swap?inputCurrency=${BETH_TOKEN}&outputCurrency=ETH&chain=ethereum`;
    } else {
      url = `https://app.uniswap.org/swap?inputCurrency=ETH&outputCurrency=0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48&chain=ethereum`;
    }
    window.open(url, '_blank');
    toast.info(`Opening Uniswap to swap ${from} → ${to}`);
  };

  const getColorClasses = (color, isSelected) => {
    const colors = {
      blue: isSelected ? 'bg-blue-900/30 border-blue-500/50 ring-2 ring-blue-500/30' : 'bg-[#0d1d35] border-[#1a2d4a] hover:border-blue-500/30',
      pink: isSelected ? 'bg-pink-900/30 border-pink-500/50 ring-2 ring-pink-500/30' : 'bg-[#0d1d35] border-[#1a2d4a] hover:border-pink-500/30',
      purple: isSelected ? 'bg-purple-900/30 border-purple-500/50 ring-2 ring-purple-500/30' : 'bg-[#0d1d35] border-[#1a2d4a] hover:border-purple-500/30'
    };
    return colors[color] || colors.blue;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="fee-conversion">
      {/* Header */}
      <div className="bg-[#0d1d35] border-b border-[#1a2d4a] px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-2">
            <Link to="/treasury">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Treasury
              </Button>
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-400" />
            Convert to USD
          </h1>
          <p className="text-gray-400 text-sm">Convert ETH or BETH to USD via bank transfer</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-900/20 to-green-900/5 border-green-500/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <p className="text-gray-400 text-xs">Total USD Converted</p>
                </div>
                <p className="text-2xl font-bold text-white">${summary.total_usd_all_time?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-blue-400" />
                  <p className="text-gray-400 text-xs">Total Records</p>
                </div>
                <p className="text-2xl font-bold text-white">{summary.total_conversions || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-blue-400" />
                  <p className="text-gray-400 text-xs">Via Coinbase</p>
                </div>
                <p className="text-2xl font-bold text-white">${summary.by_method?.coinbase?.total_usd?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-pink-400" />
                  <p className="text-gray-400 text-xs">Via Uniswap</p>
                </div>
                <p className="text-2xl font-bold text-white">${summary.by_method?.uniswap?.total_usd?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Treasury Info */}
        <Card className="bg-gradient-to-br from-green-900/20 to-green-900/5 border-green-500/30">
          <CardContent className="py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <Wallet className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Treasury Wallet</p>
                  <p className="text-white font-mono text-sm">{TREASURY_WALLET}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(TREASURY_WALLET, 'Address')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
                <a 
                  href={`https://etherscan.io/address/${TREASURY_WALLET}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View on Etherscan
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Conversions */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#b8860b]" />
                  Conversion History
                </CardTitle>
                <CardDescription>Track your off-ramp activity for accounting</CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={exportToCSV}
                  disabled={conversions.length === 0}
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={exportToPDF}
                  disabled={conversions.length === 0}
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button 
                  className="bg-[#b8860b] hover:bg-[#9a7209]"
                  size="sm"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Add Form */}
            {showAddForm && (
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg mb-4 space-y-4">
                <p className="text-white font-medium">Record New Conversion</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">From Amount</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={newConversion.from_amount}
                        onChange={(e) => setNewConversion({...newConversion, from_amount: e.target.value})}
                        className="bg-[#0a1628] border-[#2a3d5a] text-white"
                      />
                      <select
                        value={newConversion.from_currency}
                        onChange={(e) => setNewConversion({...newConversion, from_currency: e.target.value})}
                        className="px-3 rounded bg-[#0a1628] border border-[#2a3d5a] text-white"
                      >
                        <option value="ETH">ETH</option>
                        <option value="BETH">BETH</option>
                        <option value="USDC">USDC</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">To Amount</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={newConversion.to_amount}
                        onChange={(e) => setNewConversion({...newConversion, to_amount: e.target.value})}
                        className="bg-[#0a1628] border-[#2a3d5a] text-white"
                      />
                      <select
                        value={newConversion.to_currency}
                        onChange={(e) => setNewConversion({...newConversion, to_currency: e.target.value})}
                        className="px-3 rounded bg-[#0a1628] border border-[#2a3d5a] text-white"
                      >
                        <option value="USD">USD</option>
                        <option value="ETH">ETH</option>
                        <option value="USDC">USDC</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Method</label>
                    <select
                      value={newConversion.method}
                      onChange={(e) => setNewConversion({...newConversion, method: e.target.value})}
                      className="w-full p-2 rounded bg-[#0a1628] border border-[#2a3d5a] text-white"
                    >
                      <option value="coinbase">Coinbase</option>
                      <option value="uniswap">Uniswap</option>
                      <option value="moonpay">MoonPay</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">TX Hash (optional)</label>
                    <Input
                      placeholder="0x..."
                      value={newConversion.tx_hash}
                      onChange={(e) => setNewConversion({...newConversion, tx_hash: e.target.value})}
                      className="bg-[#0a1628] border-[#2a3d5a] text-white font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Notes (optional)</label>
                    <Input
                      placeholder="e.g., Q1 fee conversion"
                      value={newConversion.notes}
                      onChange={(e) => setNewConversion({...newConversion, notes: e.target.value})}
                      className="bg-[#0a1628] border-[#2a3d5a] text-white"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAddConversion} 
                    disabled={loading}
                    className="bg-[#b8860b] hover:bg-[#9a7209]"
                  >
                    {loading ? 'Saving...' : 'Save Record'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Conversion List */}
            {conversions.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No conversions recorded yet</p>
                <p className="text-gray-500 text-sm mt-1">Track your off-ramp activity for tax and accounting</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversions.map((conv) => (
                  <div 
                    key={conv.id}
                    className="flex items-center justify-between p-3 bg-[#1a2d4a]/50 rounded-lg hover:bg-[#1a2d4a] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        conv.method === 'coinbase' ? 'bg-blue-900/50' :
                        conv.method === 'uniswap' ? 'bg-pink-900/50' : 'bg-purple-900/50'
                      }`}>
                        {conv.method === 'coinbase' && <Building2 className="w-5 h-5 text-blue-400" />}
                        {conv.method === 'uniswap' && <DollarSign className="w-5 h-5 text-pink-400" />}
                        {conv.method === 'moonpay' && <CreditCard className="w-5 h-5 text-purple-400" />}
                        {conv.method === 'other' && <Wallet className="w-5 h-5 text-gray-400" />}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {conv.from_amount} {conv.from_currency} → {conv.to_amount?.toLocaleString()} {conv.to_currency}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="capitalize">{conv.method}</span>
                          <span>•</span>
                          <span>{formatDate(conv.recorded_at)}</span>
                          {conv.notes && (
                            <>
                              <span>•</span>
                              <span className="text-gray-400">{conv.notes}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {conv.tx_hash && (
                        <a 
                          href={`https://etherscan.io/tx/${conv.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteConversion(conv.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion Methods */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white">Conversion Methods</h2>
          
          {CONVERSION_METHODS.map(method => (
            <Card 
              key={method.id}
              className={`cursor-pointer transition-all ${getColorClasses(method.color, selectedMethod === method.id)}`}
              onClick={() => setSelectedMethod(method.id)}
            >
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      method.color === 'blue' ? 'bg-blue-900/50' :
                      method.color === 'pink' ? 'bg-pink-900/50' : 'bg-purple-900/50'
                    }`}>
                      {method.id === 'coinbase' && <Building2 className="w-5 h-5 text-blue-400" />}
                      {method.id === 'uniswap' && <DollarSign className="w-5 h-5 text-pink-400" />}
                      {method.id === 'moonpay' && <CreditCard className="w-5 h-5 text-purple-400" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{method.name}</p>
                        {method.recommended && (
                          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full flex items-center gap-1">
                            <Star className="w-3 h-3" /> Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">{method.description}</p>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="text-gray-500">Fee: <span className="text-white">{method.fee}</span></span>
                        <span className="text-gray-500">Time: <span className="text-white">{method.time}</span></span>
                      </div>
                    </div>
                  </div>
                  {selectedMethod === method.id && (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Coinbase Instructions */}
        {selectedMethod === 'coinbase' && (
          <Card className="bg-blue-900/20 border-blue-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-400" />
                Coinbase Conversion Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-[#b8860b] text-white text-xs rounded font-bold">BETH → USD</span>
                  <span className="text-gray-400 text-sm">2-step process</span>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <div>
                      <p className="text-white font-medium">Swap BETH → ETH on Uniswap</p>
                      <Button 
                        size="sm" 
                        className="mt-2 bg-pink-600 hover:bg-pink-700"
                        onClick={() => handleUniswapSwap('BETH', 'ETH')}
                      >
                        Open Uniswap <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <p className="text-white font-medium">Send ETH to Coinbase & Sell (see below)</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded font-bold">ETH → USD</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex gap-3">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <div>
                      <p className="text-white">Get Coinbase ETH deposit address</p>
                      <a href="https://www.coinbase.com/assets" target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="mt-1">Coinbase Assets <ExternalLink className="w-3 h-3 ml-1" /></Button>
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <p className="text-white">Send ETH from MetaMask to Coinbase</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <div>
                      <p className="text-white">Sell ETH for USD</p>
                      <a href="https://www.coinbase.com/trade" target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="mt-1">Coinbase Trade <ExternalLink className="w-3 h-3 ml-1" /></Button>
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                    <p className="text-white">Withdraw USD to bank (ACH free, Wire ~$25)</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                <p className="text-white font-medium flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Pro Tips</p>
                <ul className="text-sm text-gray-400 mt-1 space-y-1">
                  <li>• Use limit orders for better rates</li>
                  <li>• ACH is free, Wire is faster (~$25)</li>
                  <li>• Coinbase Pro has lower fees</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Uniswap Instructions */}
        {selectedMethod === 'uniswap' && (
          <Card className="bg-pink-900/20 border-pink-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-pink-400" />
                Swap to Stablecoin
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-400 text-sm">Convert to USDC and off-ramp later via Coinbase</p>
              <div className="grid grid-cols-2 gap-3">
                <Button className="bg-pink-600 hover:bg-pink-700" onClick={() => handleUniswapSwap('BETH', 'ETH')}>
                  BETH → ETH <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <Button className="bg-pink-600 hover:bg-pink-700" onClick={() => handleUniswapSwap('ETH', 'USDC')}>
                  ETH → USDC <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* MoonPay Instructions */}
        {selectedMethod === 'moonpay' && (
          <Card className="bg-purple-900/20 border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-400" />
                MoonPay Off-Ramp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <p className="text-white font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" /> Business Account Required
                </p>
                <p className="text-sm text-gray-400">KYB verification takes 2-5 days. Higher fees than Coinbase.</p>
              </div>
              <a href="https://www.moonpay.com/business" target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-purple-600 hover:bg-purple-700">
                  Register for MoonPay Business <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FeeConversion;
